import React, { useCallback, useState } from 'react';
import { Upload, FileText, X, WarningCircle } from '@phosphor-icons/react';
import { cn } from "@/lib/utils";

/**
 * Dropzone - Governed Upload Component
 * 
 * @param {Function} onFileSelect - Callback(file)
 * @param {string} accept - File type (e.g. ".docx")
 * @param {boolean} disabled - Disable interaction
 * @param {boolean} processing - Show processing state
 * @param {string} error - Show error message
 * @param {File} file - Current selected file (controlled)
 * @param {Function} onClear - Callback to clear file
 */
export function Dropzone({
    onFileSelect,
    accept = "*",
    disabled = false,
    processing = false,
    error = null,
    file = null,
    onClear,
    className
}) {
    const [isDragOver, setIsDragOver] = useState(false);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        if (!disabled && !processing) setIsDragOver(true);
    }, [disabled, processing]);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (disabled || processing) return;

        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles?.length > 0) {
            onFileSelect(droppedFiles[0]);
        }
    }, [disabled, processing, onFileSelect]);

    const handleFileInput = useCallback((e) => {
        const selectedFiles = e.target.files;
        if (selectedFiles?.length > 0) {
            onFileSelect(selectedFiles[0]);
        }
    }, [onFileSelect]);

    // 1. File Selected State
    if (file) {
        return (
            <div className={cn(
                "border border-slate-200 rounded-lg p-3 flex items-center justify-between bg-slate-50",
                className
            )}>
                <div className="flex items-center gap-3 overflow-hidden">
                    <div className="bg-white p-2 rounded border border-slate-200">
                        <FileText className="h-5 w-5 text-brand-900" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                            {file.name}
                        </p>
                        <p className="text-xs text-slate-500">
                            {(file.size / 1024).toFixed(1)} KB
                        </p>
                    </div>
                </div>
                {!processing && onClear && (
                    <button
                        onClick={onClear}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        type="button"
                    >
                        <X className="h-5 w-5" />
                    </button>
                )}
            </div>
        );
    }

    // 2. Upload/Drop State
    return (
        <div
            className={cn(
                "relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200",
                isDragOver ? "border-brand-500 bg-brand-50" : "border-gray-300 hover:bg-slate-50",
                error ? "border-red-300 bg-red-50" : "",
                (disabled || processing) ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                className
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <input
                type="file"
                accept={accept}
                onChange={handleFileInput}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                disabled={disabled || processing}
            />

            <div className="flex flex-col items-center justify-center pointer-events-none">
                {error ? (
                    <WarningCircle className="h-10 w-10 text-red-500 mb-3" />
                ) : (
                    <Upload className={cn(
                        "h-10 w-10 mb-3 transition-colors",
                        isDragOver ? "text-brand-900" : "text-slate-400"
                    )} />
                )}

                <p className={cn(
                    "text-base font-medium",
                    error ? "text-red-700" : "text-gray-900"
                )}>
                    {error || "Click to upload or drag and drop"}
                </p>

                {!error && (
                    <p className="text-xs text-slate-500 mt-1">
                        {accept}
                    </p>
                )}
            </div>
        </div>
    );
}
